import { Controller } from "@hotwired/stimulus"

// The running recap beside the Riachuelo catálogue wizard: what has been picked
// so far, updated live as the operator moves through the steps.
//
// Hidden on step 1 so the model grid gets the full width, then it slides in as
// a col-3 rail from step 2 onward. Purely a read-out — it owns no field and
// changes no value, so the form submits exactly the same payload with or
// without it (and with or without JS).
export default class extends Controller {
  static targets = [
    "panel", "main",
    "model", "modelThumb", "modelName", "modelBand",
    "items", "itemCount", "itemList",
    "background", "backgroundSwatch", "backgroundName",
    "poses", "poseCount", "poseList",
    "total"
  ]

  connect() {
    this.refresh()
  }

  // form-wizard:step — step 1 is the model picker on its own.
  stepChanged(event) {
    const onFirstStep = event.detail.index === 0
    if (this.hasPanelTarget) this.panelTarget.hidden = onFirstStep
    if (this.hasMainTarget) {
      this.mainTarget.classList.toggle("col-lg-9", !onFirstStep)
      this.mainTarget.classList.toggle("col-12", onFirstStep)
    }
    this.refresh()
  }

  refresh() {
    this.refreshModel()
    this.refreshItems()
    this.refreshBackground()
    this.refreshPoses()
    this.refreshTotal()
  }

  // --- sections -----------------------------------------------------------

  refreshModel() {
    if (!this.hasModelTarget) return
    const picked = this.el('input[name="brand_model_id"]:checked')
    this.toggle(this.modelTarget, !!picked)
    if (!picked) return

    const card = document.querySelector(`label[for="${picked.id}"]`)
    const img  = card?.querySelector("img")
    if (this.hasModelThumbTarget) {
      if (img) { this.modelThumbTarget.src = img.src; this.modelThumbTarget.hidden = false }
      else { this.modelThumbTarget.hidden = true }
    }
    this.text(this.modelNameTarget, card?.querySelector(".rc-name")?.textContent?.trim())
    this.text(this.modelBandTarget, card?.querySelector(".rc-meta")?.textContent?.trim())
  }

  refreshItems() {
    if (!this.hasItemsTarget) return
    // A row counts once it has a front picture or a front URL — the same rule
    // the controller uses to drop empty rows server-side.
    const rows = Array.from(document.querySelectorAll("[data-garment-pieces-target='row']"))
    const filled = rows.filter((row) => {
      if (row.querySelector("input[name*='[_destroy]']")?.value === "1") return false
      const file = row.querySelector("input[type='file'][name*='[front_picture]']")
      const url  = row.querySelector("input[name*='[front_url]']")
      return (file && file.files && file.files.length > 0) || (url && url.value.trim() !== "")
    })

    this.toggle(this.itemsTarget, filled.length > 0)
    this.text(this.itemCountTarget, `${filled.length} item${filled.length === 1 ? "" : "s"}`)

    if (!this.hasItemListTarget) return
    this.itemListTarget.innerHTML = ""
    filled.forEach((row) => {
      const select = row.querySelector("select[name*='[category]']")
      const label  = row.querySelector("input[name*='[label]']")?.value?.trim()
      const name   = label || select?.options[select.selectedIndex]?.text || "Item"
      this.itemListTarget.appendChild(this.chip(name))
    })
  }

  refreshBackground() {
    if (!this.hasBackgroundTarget) return
    const picked = this.el('input[name="background_id"]:checked')
    this.toggle(this.backgroundTarget, !!picked)
    if (!picked) return

    let hex, name
    if (picked.value === "custom") {
      hex  = this.el('input[name="custom_background_hex"]')?.value?.trim() || ""
      name = hex ? "Custom" : "Custom — type a hex"
      if (hex && !hex.startsWith("#")) hex = `#${hex}`
    } else {
      const card = document.querySelector(`label[for="${picked.id}"]`)
      name = card?.querySelector(".rc-name")?.textContent?.trim()
      hex  = card?.querySelector(".rc-meta")?.textContent?.trim()
    }

    if (this.hasBackgroundSwatchTarget) {
      const valid = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex || "")
      this.backgroundSwatchTarget.style.background = valid ? hex : "transparent"
      this.backgroundSwatchTarget.style.borderStyle = valid ? "solid" : "dashed"
    }
    this.text(this.backgroundNameTarget, [name, hex].filter(Boolean).join(" · "))
  }

  refreshPoses() {
    if (!this.hasPosesTarget) return
    const picked = Array.from(document.querySelectorAll('input[name="pose_ids[]"]:checked'))
      // A pose hidden by the age-group filter is unticked by that filter, but
      // guard anyway so the recap never counts something invisible.
      .filter((cb) => !cb.closest("[data-pose-card]")?.hidden)

    this.toggle(this.posesTarget, picked.length > 0)
    this.text(this.poseCountTarget, `${picked.length} pose${picked.length === 1 ? "" : "s"}`)

    if (!this.hasPoseListTarget) return
    this.poseListTarget.innerHTML = ""
    picked.slice(0, 8).forEach((cb) => {
      const name = document.querySelector(`label[for="${cb.id}"] .rc-name`)?.textContent?.trim()
      if (name) this.poseListTarget.appendChild(this.chip(name))
    })
    if (picked.length > 8) this.poseListTarget.appendChild(this.chip(`+${picked.length - 8} more`))
  }

  refreshTotal() {
    if (!this.hasTotalTarget) return
    const n = document.querySelectorAll('input[name="pose_ids[]"]:checked').length
    this.totalTarget.textContent = n === 0
      ? "Pick at least one pose"
      : `${n} picture${n === 1 ? "" : "s"} will be generated`
  }

  // --- helpers ------------------------------------------------------------

  el(selector) { return document.querySelector(selector) }

  text(node, value) { if (node) node.textContent = value || "—" }

  toggle(node, on) { if (node) node.hidden = !on }

  chip(text) {
    const span = document.createElement("span")
    span.className = "rc-recap__chip"
    span.textContent = text
    return span
  }
}
